import React from "react";
import { Link } from "react-router-dom";

import { InvestmentsWorkspaceShell } from "@features/investments/components/workspace-shell";
import { InvestmentsLoadingScreen, InvestmentsPageState } from "@features/investments/components/page-state";
import { GlossaryTerm } from "@features/investments/glossary/term";
import { useInvestmentsAccessQuery } from "@features/investments/queries";
import "@views/investments/index.scss";

const HelpScreen = (): JSX.Element => {
  const accessQuery = useInvestmentsAccessQuery();
  const isOperator = accessQuery.data?.isGod === true;

  if (accessQuery.isLoading) {
    return <InvestmentsLoadingScreen message="Loading help guide..." />;
  }

  if (accessQuery.error) {
    return <InvestmentsPageState message={`Failed to resolve investments access: ${accessQuery.error.message}`} />;
  }

  return (
    <InvestmentsWorkspaceShell
      title="Help / FAQ"
      description="How to navigate the Investments app, what each tab is for, and how to turn research into decisions."
      headerMeta={<div className="dashboard-generated-at">Reference guide</div>}
    >
      <section className="workflow-section">
        <div className="workflow-section-header">
          <div>
            <h2>How To Use This App</h2>
            <p>Use this page as the practical operating guide. It explains the tabs, when to use each workspace, and what a normal research flow looks like.</p>
          </div>
        </div>

        <div className="reference-tab-strip" role="tablist" aria-label="Reference sections">
          <Link className="reference-tab-link" to="/investments/reference/glossary">Glossary</Link>
          <Link className="reference-tab-link" to="/investments/reference/method">Method</Link>
          <span className="reference-tab-link active">Help</span>
        </div>

        <div className="reference-method-grid">
          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Start Here</div>
            <h3>Most users should begin in Coverage, not in a detail page</h3>
            <p>
              Coverage is the front door. Search for a ticker, filter by family or action, then open a detail page when a name deserves deeper work.
              If you need context for hurdle rates, open Macro before deciding whether the current setup is attractive.
            </p>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Modes</div>
            <h3>Snapshot Research is for reading, Operator Mode is for running workflow</h3>
            <p>
              Snapshot Research gives every authorized user cached research, macro context, ETF coverage, and detail pages.
              Operator Mode adds live quotes, refresh, Command, and Portfolio workflow for active tracking.
            </p>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Main Tabs</div>
            <h3>What each top-level tab is for</h3>
            <ul className="reference-help-list">
              <li><strong>Coverage:</strong> compare equities or ETFs and decide what is worth opening next.</li>
              <li><strong>Macro:</strong> judge whether current Treasury yields make the setup more or less demanding.</li>
              {isOperator && <li><strong>Command:</strong> start-of-day operator page for reviews, candidate timing, owned-position drift, and macro pressure.</li>}
              {isOperator && <li><strong>Portfolio:</strong> manage active research, candidate, and owned names with thesis notes and review history.</li>}
              <li><strong>Reference:</strong> use Glossary for term definitions, Method for system logic, and Help for operating instructions.</li>
            </ul>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Coverage</div>
            <h3>Use Coverage to narrow the field before committing time</h3>
            <p>
              The Coverage asset switch separates equities from ETFs. Equities are for business-level underwriting. ETFs are for exposure, cost, liquidity, and portfolio-role decisions.
            </p>
            <p>
              Example: filter equities to a family, sort by <GlossaryTerm label="FCF-10Y" />, and open the top two names whose action and score still look attractive under current rates.
            </p>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Macro</div>
            <h3>Use Macro when you want rate discipline, not when you want a ticker thesis</h3>
            <p>
              Macro is not a stock picker. It tells you what hurdle the market environment is imposing. The key question is whether the cash-yield spread still clears the current rate backdrop.
            </p>
            <p>
              Example: a company can look high quality and still be a weak setup if its current <GlossaryTerm label="FCF-10Y" /> spread is negative while the buy threshold is getting tighter.
            </p>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Detail Pages</div>
            <h3>What the detail-page tabs are for</h3>
            <ul className="reference-help-list">
              <li><strong>Overview:</strong> fast read on the business, action, valuation zone, and current context.</li>
              <li><strong>Fundamentals:</strong> deeper look at revenue, margins, cash flow, balance sheet, and quality signals.</li>
              <li><strong>Entry:</strong> focus on buy levels, valuation discipline, and what would make the setup attractive.</li>
              <li><strong>Risk:</strong> focus on the key risk framing, red flags, and what could break the thesis.</li>
              <li><strong>DCF Lab:</strong> scenario-testing workspace, not a summary page.</li>
              <li><strong>History:</strong> time series and historical context when you need to verify trend durability.</li>
            </ul>
          </section>

          {isOperator && (
            <section className="investments-card workflow-card reference-method-card">
              <div className="section-eyebrow">Command</div>
              <h3>Use Command to decide what needs attention first</h3>
              <p>
                Command is the operator dashboard. It is for prioritization, not for full analysis. Start there when you want to know what to review today, which candidates are nearing buy range, and which owned positions are drifting.
              </p>
              <p>
                Example: open Command in the morning, review overdue names first, then jump into the underlying ticker detail or Portfolio workflow from the queued names.
              </p>
            </section>
          )}

          {isOperator && (
            <section className="investments-card workflow-card reference-method-card">
              <div className="section-eyebrow">Portfolio</div>
              <h3>Use Portfolio to track thesis, stage, and review cadence</h3>
              <p>
                Portfolio is where names become workflow objects. Move a ticker into <GlossaryTerm label="Portfolio Stage" />, write the thesis summary, note the key risk, and set the next review date.
              </p>
              <p>
                Example: after researching NVDA in Coverage and detail, add it to Portfolio as Active Research, write why now, then promote it to Candidate or Owned as conviction and price improve.
              </p>
            </section>
          )}

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Example Workflow</div>
            <h3>One practical way to use the product</h3>
            <ol className="reference-help-list reference-help-list-numbered">
              <li>Start in Coverage and search for a sector, family, or ticker.</li>
              <li>Open the detail page for the most interesting candidate and read Overview, Entry, and Risk.</li>
              <li>Open Macro if you need to check whether the current setup still clears rate discipline.</li>
              {isOperator ? (
                <>
                  <li>Add the name to Portfolio, choose a stage, and write the thesis summary plus key risk.</li>
                  <li>Use Command later to see whether the name needs review or has moved closer to action.</li>
                </>
              ) : (
                <li>Use Reference pages to clarify any formula or workflow term you do not recognize.</li>
              )}
            </ol>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">FAQ</div>
            <h3>Common questions</h3>
            <div className="reference-help-faq">
              <div>
                <strong>Why don&apos;t I see live quotes?</strong>
                <p>Live quotes are only available in Operator Mode. Snapshot Research intentionally stays cached and read-only.</p>
              </div>
              <div>
                <strong>When should I use ETFs instead of equities?</strong>
                <p>Use ETFs when the decision is about exposure or role. Use equities when the decision depends on business quality, cash flow, valuation, and company-specific risk.</p>
              </div>
              <div>
                <strong>What should I do when a term is unclear?</strong>
                <p>Open Glossary for definitions and Method for the system logic behind the labels. Help is the operating guide, not the term dictionary.</p>
              </div>
            </div>
          </section>
        </div>
      </section>
    </InvestmentsWorkspaceShell>
  );
};

export default HelpScreen;
